'use memo';

import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography';
import type {
  ChatMessageUsage,
  Citation,
} from '@/features/global-ai-chat/api/types';
import {
  formatGuardrailUsdEstimate,
  formatTokenCount,
  toChatMessageUsageDisplay,
} from '@/features/global-ai-chat/utils/formatChatMessageUsage';
import { parseChatMessageUsage } from '@/features/global-ai-chat/utils/parseChatMessageUsage';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { AiChatMarkdown } from './AiChatMarkdown';

type AiChatMessageBubbleProps = {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  citations?: Citation[];
  usage?: ChatMessageUsage | null;
  /**
   * 確実に回答できたか（GW が構造化出力から確定し API が型付きで返す）。
   * false のときのみ「DRESS CODE 社員に確認」等の補足動線を出す。未指定は true 扱い。
   * （旧: content の生 JSON をフロントで再パースしていたのを廃止し、API の確定値を使う）
   */
  canAnswer?: boolean;
};

export function AiChatMessageBubble({
  role,
  content,
  timestamp,
  citations,
  usage,
  canAnswer,
}: AiChatMessageBubbleProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'component.globalAiChat',
  });
  const isUser = role === 'user';
  const hasCitations = !isUser && citations && citations.length > 0;
  const parsedUsage = parseChatMessageUsage(usage);
  const hasUsage = !isUser && parsedUsage != null;
  // content は GW が整形した表示用 markdown 本文。canAnswer は API 由来の確定値。
  const answer = content;
  // アシスタント回答で canAnswer が明示 false のときだけ補足動線を出す。
  const showUnresolved = !isUser && canAnswer === false;

  return (
    <div
      className={cn(
        'flex flex-col gap-1',
        isUser ? 'items-end' : 'items-start',
      )}
    >
      <div
        className={cn(
          'max-w-[88%] rounded-2xl border px-3 py-2',
          isUser
            ? 'rounded-br-none border-primary-muted-border bg-primary-muted-fill'
            : 'rounded-bl-none border-neutral-muted-border bg-neutral-bottom-fill',
        )}
      >
        {isUser ? (
          <Text
            variant="pSmall"
            className="whitespace-pre-wrap text-neutral-on-fill"
          >
            {content}
          </Text>
        ) : (
          <>
            <AiChatMarkdown content={answer} />
            {showUnresolved ? (
              <Text
                variant="pSmall"
                className="mt-2 block text-neutral-on-fill"
              >
                {t('unresolvedContactSupport')}
              </Text>
            ) : null}
          </>
        )}

        {hasCitations ? (
          <div className="mt-2 flex flex-col gap-1 border-t border-neutral-muted-border pt-2">
            <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
              {t('citationsLabel')}
            </Text>
            <ul className="flex flex-col gap-1">
              {citations.map((citation, index) => (
                <CitationItem
                  key={citation.sourceUri ?? `${role}-citation-${index}`}
                  citation={citation}
                />
              ))}
            </ul>
          </div>
        ) : null}

        {hasUsage ? <TokenUsageBlock usage={parsedUsage} /> : null}
      </div>
      <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
        {timestamp}
      </Text>
    </div>
  );
}

function TokenUsageBlock({ usage }: { usage: ChatMessageUsage }) {
  const { t, i18n } = useTranslation(defaultNS, {
    keyPrefix: 'component.globalAiChat',
  });
  const locale = i18n.language;
  const display = toChatMessageUsageDisplay(usage);
  const guardrailNote =
    usage.guardrailBlockedAt === 'input'
      ? t('tokenUsageGuardrailBlockedInput')
      : usage.guardrailBlockedAt === 'output'
        ? t('tokenUsageGuardrailBlockedOutput')
        : null;

  return (
    <div
      className={cn(
        'mt-2 flex flex-col gap-1',
        'border-t border-neutral-muted-border pt-2',
      )}
    >
      <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
        {t('tokenUsageLabel')}
      </Text>
      <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
        {t('tokenUsageIntro')}
      </Text>
      <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
        {t('tokenUsageSummary', {
          formattedTotal: formatTokenCount(display.totalTokens, locale),
        })}
      </Text>
      <ul className="flex flex-col gap-0.5 pl-3">
        <li>
          <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
            {t('tokenUsageInput', {
              formattedCount: formatTokenCount(display.inputTokens, locale),
            })}
          </Text>
        </li>
        <li>
          <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
            {t('tokenUsageOutput', {
              formattedCount: formatTokenCount(display.outputTokens, locale),
            })}
          </Text>
        </li>
        {display.guardrailEstimatedUsd !== undefined ? (
          <li>
            <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
              {t('tokenUsageSafetyCheckCost', {
                amount: formatGuardrailUsdEstimate(
                  display.guardrailEstimatedUsd,
                ),
              })}
            </Text>
          </li>
        ) : null}
      </ul>
      {guardrailNote ? (
        <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
          {guardrailNote}
        </Text>
      ) : null}
    </div>
  );
}

function CitationItem({ citation }: { citation: Citation }) {
  const label = citation.content.trim() || citation.sourceUri || '';
  if (citation.sourceUri) {
    return (
      <li>
        <a
          href={citation.sourceUri}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'inline-flex items-center gap-1 text-primary-on-neutral-fill',
            'hover:underline focus-visible:outline-hidden focus-visible:underline',
          )}
        >
          <Icon iconName="ExternalLinkIcon" size={12} inheritColor />
          <Text
            variant="labelExSmall"
            className="line-clamp-1 text-primary-on-neutral-fill"
          >
            {label}
          </Text>
        </a>
      </li>
    );
  }
  return (
    <li>
      <Text
        variant="labelExSmall"
        className="line-clamp-1 text-neutral-muted-on-fill"
      >
        {label}
      </Text>
    </li>
  );
}
